import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  Spinner,
  Select,
  Textarea,
  Button,
  Center,
  InputLeftElement,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  DefaultValues,
  Path,
  RegisterOptions,
  UseFormReturn,
  useForm as useReactHookForm,
} from "react-hook-form";
import { assets } from "../assets";

type BaseFormField = {
  value: string;
  label?: string;
  placeholder?: string;
  validation?: RegisterOptions;
};

type EmailField = BaseFormField & {
  type: "email";
};

type PasswordField = BaseFormField & {
  type: "password";
  showEye: boolean;
};

type TextField = BaseFormField & {
  type: "text";
};

type SelectionField = BaseFormField & {
  type: "selection";
  options: string[];
  defaultValue?: string;
};

type FileField = BaseFormField & {
  type: "file";
  accept: string;
  multiple?: boolean;
  validation?: RegisterOptions;
};

type TextAreaField = BaseFormField & {
  type: "textarea";
  rows?: number;
};

export type FormField =
  | EmailField
  | PasswordField
  | TextField
  | SelectionField
  | FileField
  | TextAreaField;

export type FormFields<T extends string> = {
  [K in T]: FormField;
};

type InputSize = "lg" | "md" | "sm" | "xs";
type InputVariant = "outline" | "filled" | "flushed" | "unstyled";

interface IProps<T extends string> {
  initialValues: FormFields<T>;
  showLabel: boolean;
  onSubmit: (data: { [K in T]: any }) => void;
  submitLabel: string;
  loading?: boolean;
  isEditMode?: boolean;
  currentChanges?: Partial<Record<T, any>>;
  inputSize?: InputSize;
  variant?: InputVariant;
  showButton?: boolean;
  buttonWidth?: string;
}

type IRenderProps = {
  Header?: React.ReactElement;
  Body?: React.ReactElement;
  Footer?: React.ReactElement;
};

const useForm = <T extends string>(props: IProps<T>) => {
  const { showButton = true } = props;
  const defaultValues = Object.fromEntries(
    Object.entries(props.initialValues).map(([key, field]) => {
      const typedField = field as FormField;
      return [
        key,
        typedField.type === "selection" && "defaultValue" in typedField
          ? typedField.defaultValue
          : typedField.value,
      ];
    })
  ) as DefaultValues<FormFields<T>>;

  const form = useReactHookForm<FormFields<T>>({
    mode: "onChange",
    defaultValues,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(
    props.isEditMode !== undefined ? props.isEditMode : true
  );
  const isValid = form.formState.isValid;
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    setLoading(props.loading ?? false);
  }, [props.loading]);

  React.useEffect(() => {
    if (props.isEditMode !== undefined) {
      setIsEditMode(props.isEditMode);
    }
  }, [props.isEditMode]);

  React.useEffect(() => {
    if (props.currentChanges) {
      if (Object.keys(props.currentChanges).length === 0) {
        setChangedFields(new Set());
      } else {
        const changedKeys = Object.keys(props.currentChanges) as string[];
        setChangedFields(new Set(changedKeys));
      }
    } else {
      setChangedFields(new Set());
    }
  }, [props.currentChanges]);

  const handleSubmit = (data: FormFields<T>) => {
    props.onSubmit(data);
  };

  const render = ({ Header, Body, Footer }: IRenderProps = {}) => {
    return (
      <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
        {Header ?? null}
        <Box display={"flex"} gap={{ base: 4, lg: 3 }} flexDirection={"column"}>
          {(Object.entries(props.initialValues) as [T, FormField][]).map(
            ([key, formField]) => (
              <Box key={key}>
                {renderField({
                  fieldName: key as unknown as Path<FormFields<T>>,
                  formField,
                  form,
                  isDisabled: !isEditMode,
                  changedFields,
                  showLabel: props.showLabel,
                  size: props?.inputSize,
                  variant: props?.variant,
                })}
              </Box>
            )
          )}
          {Body ?? null}
          {isEditMode && showButton && (
            <Button
              // isLoading={loading}
              variant={"secondary"}
              type="submit"
              width={props.buttonWidth ?? "fit"}
              size={"lg"}
              mx={"auto"}
              isDisabled={!isValid || loading}
            >
              {loading ? <Spinner size="sm" /> : props.submitLabel}
            </Button>
          )}
        </Box>
        {Footer ?? null}
      </form>
    );
  };

  return {
    render,
    form,
    loading,
    setLoading,
  };
};

const renderField = <T extends string>({
  fieldName,
  formField,
  form,
  isDisabled,
  changedFields,
  showLabel,
  size,
  variant,
}: {
  fieldName: Path<FormFields<T>>;
  formField: FormField;
  form: UseFormReturn<FormFields<T>, any, undefined>;
  isDisabled: boolean;
  changedFields: Set<string>;
  showLabel?: boolean;
  size?: InputSize;
  variant?: InputVariant;
}) => {
  const [eyeOpen, setEye] = useState<boolean>(false);
  const errorMessage = form.formState.errors[fieldName]?.message as
    | string
    | null;

  const commonProps = {
    isDisabled: isDisabled,
    isReadOnly: isDisabled,
    size,
    variant,
  };

  const commonStyle = () =>
    useMemo(() => {
      if (changedFields.has(fieldName)) {
        return {
          borderColor: "green",
          bg: "green.900",
          variant,
        };
      }
      return {};
    }, [changedFields, fieldName]);

  if (formField.type === "email") {
    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{formField.label}</FormLabel>
        )}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Image src={assets.icons.emailOutline} ms=".5rem" mt={".5rem"} />
          </InputLeftElement>
          <Input
            {...form.register(fieldName, {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
              maxLength: {
                value: 75,
                message: "Email must be 75 characters or less",
              },
              ...formField.validation,
            } as any)}
            type="email"
            placeholder={formField.placeholder ?? "Enter email"}
            {...commonProps}
            {...commonStyle()}
          />
        </InputGroup>
        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  } else if (formField.type === "password") {
    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{formField.label}</FormLabel>
        )}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Image src={assets.icons.lockOutline} ms={".5rem"} mt={".5rem"} />
          </InputLeftElement>
          <Input
            {...form.register(fieldName, {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be 6 chars",
              },
              pattern: {
                value: /^(?=.*[!@#$%^&*])/,
                message: "Password must contain at least one special character",
              },
              ...formField.validation,
            } as any)}
            type={eyeOpen || !formField.showEye ? "text" : "password"}
            placeholder={formField.placeholder ?? "Enter password"}
            {...commonProps}
            {...commonStyle()}
          />
          {formField.showEye && (
            <InputRightElement
              onClick={() => setEye(!eyeOpen)}
              cursor="pointer"
            >
              <Center>
                <Image
                  mt={".5rem"}
                  h={"1.8rem"}
                  mr={".5rem"}
                  src={
                    eyeOpen
                      ? assets.icons.passwordShowIcon
                      : assets.icons.passwordHideIcon
                  }
                />
              </Center>
            </InputRightElement>
          )}
        </InputGroup>
        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  } else if (formField.type === "selection") {
    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{formField.label}</FormLabel>
        )}
        <Select
          {...form.register(fieldName, formField.validation as any)}
          placeholder={formField.placeholder ?? "Select an option"}
          defaultValue={formField.defaultValue}
          {...commonProps}
          {...commonStyle()}
        >
          {formField.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  }
  if (formField.type === "file") {
    const fileField = formField as FileField;
    const fileValue = form.watch(fieldName);

    // Function to get preview URL
    const getPreviewURL = () => {
      if (fileValue instanceof FileList && fileValue[0]) {
        return URL.createObjectURL(fileValue[0]);
      }
      if (typeof fileValue === "string") {
        return fileValue;
      }
      return "";
    };

    // Cleanup object URL on unmount or when file changes
    React.useEffect(() => {
      const previewURL = getPreviewURL();
      return () => {
        if (previewURL && previewURL.startsWith("blob:")) {
          URL.revokeObjectURL(previewURL);
        }
      };
    }, [fileValue]);

    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{fileField.label}</FormLabel>
        )}
        <Input
          type="file"
          accept={fileField.accept}
          multiple={fileField.multiple}
          {...form.register(fieldName, {
            validate: (value: any) => {
              const currentValue = form.getValues(fieldName as any);
              if (typeof currentValue === "string" && currentValue) {
                // If there's an existing URL, consider it valid
                return true;
              }
              // Use the validation from formField if it exists
              if (fileField.validation?.validate) {
                if (typeof fileField.validation.validate === "function") {
                  return fileField.validation.validate(fieldName, value);
                } else if (typeof fileField.validation.validate === "object") {
                  // If it's an object of validation functions, run all of them
                  for (const validateFn of Object.values(
                    fileField.validation.validate
                  )) {
                    const result = validateFn(fieldName, value);
                    if (result !== true) return result;
                  }
                }
              }
              return true;
            },
          })}
          {...commonProps}
          {...commonStyle()}
        />

        {getPreviewURL() && (
          <Box my={"1rem"}>
            <Box fontSize="sm" mb={2}>
              Current file:
            </Box>
            <Image
              mx="auto"
              maxH="13rem"
              objectFit="contain"
              src={getPreviewURL()}
              alt="Preview"
              borderRadius="base"
              border="1px solid"
              p={"1rem"}
              borderColor="neutral.800"
            />
          </Box>
        )}

        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  } else if (formField.type === "textarea") {
    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{formField.label}</FormLabel>
        )}
        <Textarea
          {...form.register(fieldName, formField.validation as any)}
          placeholder={formField.placeholder ?? "Enter text"}
          rows={(formField as TextAreaField).rows || 3}
          {...commonProps}
          {...commonStyle()}
        />
        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  } else {
    return (
      <FormControl isInvalid={!!form.formState.errors[fieldName]?.message}>
        {showLabel && (
          <FormLabel fontSize={"0.875rem"}>{formField.label}</FormLabel>
        )}
        <Input
          {...form.register(fieldName, formField.validation as any)}
          type="text"
          placeholder={formField.placeholder ?? "Enter"}
          {...commonProps}
          {...commonStyle()}
        />
        <FormErrorMessage color={"red.400"}>{errorMessage}</FormErrorMessage>
      </FormControl>
    );
  }
};

export default useForm;
